import { t } from "@travily/i18n";
import communityIllustration from "@ui/assets/illustrations/community-animated.webp";
import { ResponsiveText } from "@ui/components/ResponsiveText";
import { TileRadioGroup } from "@ui/components/TileRadioGroup";
import { Wizard } from "@ui/components/Wizard";
import { useBreakpoint } from "@ui/hooks/use-breakpoint";
import type { ReactNode } from "react";

const COLOR = "yellow" as const;

type NewsletterConsent = "yes" | "no";

export interface FoundingMemberSubscribeStepViewProps {
  /** Current stage index for the wizard progress indicator. */
  currentStage: number;
  /** Total number of stages in the wizard. */
  totalStages: number;
  /** Currently selected newsletter-consent answer, if any. */
  value: NewsletterConsent | null;
  /** Field-level validation error to surface beneath the options. */
  error?: string;
  /** Whether the primary (continue) action is in flight. */
  primaryPending: boolean;
  /** Tertiary top-bar actions (e.g. "Questions?" / "Save and finish later"). */
  topBarActions?: ReactNode;
  /** Navigate to the previous stage. Omitted on the first stage. */
  onBack?: () => void;
  /** Submit the current stage and advance. */
  onPrimary: () => void;
  /** Called when the member picks a newsletter-consent option. */
  onSelectConsent: (value: NewsletterConsent) => void;
}

export function FoundingMemberSubscribeStepView({
  currentStage,
  totalStages,
  value,
  error,
  primaryPending,
  topBarActions,
  onBack,
  onPrimary,
  onSelectConsent,
}: FoundingMemberSubscribeStepViewProps) {
  const isAboveMd = useBreakpoint("md");
  const isAboveLg = useBreakpoint("lg");
  const selectionGroupSize = isAboveLg ? "5xl" : isAboveMd ? "4xl" : "3xl";

  return (
    <Wizard color={COLOR} currentStage={currentStage} totalStages={totalStages} onBack={onBack}>
      {topBarActions}
      <Wizard.PrimaryButton
        isPending={primaryPending}
        mobileLabel={t("web.founding_members.creator.steps.community.subscribe.show.button.base")}
        onPress={onPrimary}
      >
        {t("web.founding_members.creator.steps.community.subscribe.show.button.md")}
      </Wizard.PrimaryButton>
      <Wizard.Stage
        illustration={
          <img alt="" aria-hidden="true" className="h-[168px] w-[112px] object-contain" src={communityIllustration} />
        }
        heading={t("web.founding_members.creator.steps.community.subscribe.show.title")}
        helper={t("web.founding_members.creator.steps.community.subscribe.show.subtitle")}
      >
        <TileRadioGroup
          className="w-full max-w-[600px]"
          color={COLOR}
          onChange={(selected) => onSelectConsent(selected as NewsletterConsent)}
          size={selectionGroupSize}
          value={value ?? undefined}
        >
          <TileRadioGroup.Item value="yes">
            <ResponsiveText
              base={t("web.founding_members.creator.steps.community.subscribe.show.options.yes.base")}
              sm={t("web.founding_members.creator.steps.community.subscribe.show.options.yes.sm")}
            />
          </TileRadioGroup.Item>
          <TileRadioGroup.Item value="no">
            <ResponsiveText
              base={t("web.founding_members.creator.steps.community.subscribe.show.options.no.base")}
              sm={t("web.founding_members.creator.steps.community.subscribe.show.options.no.sm")}
            />
          </TileRadioGroup.Item>
        </TileRadioGroup>
        {error ? <Wizard.FieldError>{error}</Wizard.FieldError> : null}
      </Wizard.Stage>
    </Wizard>
  );
}
